import { useState, useEffect } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { TruncatedText } from "@/components/ui/truncated-text";
import { DeleteBrandDialog, DeleteBrandTarget } from "@/components/shared/DeleteBrandDialog";
import { useAppData } from "@/contexts/AppDataContext";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import { RoutePath } from "@/routes";
import { ACCOUNT_ROLE } from "@/constants";
import { accountApi } from "@/resources/account";

export function BrandSelector() {
  const navigate = useNavigate();
  const location = useLocation();
  const { brands, selectedBrandId, setSelectedBrandId } = useAppData();
  const [open, setOpen] = useState(false);
  const [brandCommandValue, setBrandCommandValue] = useState("");
  const [brandToDelete, setBrandToDelete] = useState<DeleteBrandTarget | null>(null);

  const { data: account } = accountApi.useGetAccount();
  const canMutateBrands =
    account?.role === ACCOUNT_ROLE.OWNER ||
    account?.role === ACCOUNT_ROLE.ADMIN;
  const readOnly = useViewerReadOnly();

  const currentBrand = brands.find((b) => b.id === selectedBrandId) || brands[0];

  useEffect(() => {
    if (!open) return;
    const active = brands.find((b) => b.id === selectedBrandId) ?? brands[0];
    setBrandCommandValue(active?.name ?? "");
  }, [open, brands, selectedBrandId]);

  const handleBrandSelect = (brandId: number) => {
    const isSwitch = brandId !== currentBrand?.id;
    setSelectedBrandId(brandId);
    setOpen(false);
    // AUT-438 design QA: confirm the switch, with the prototype's copy (signal-v3 AppSidebar). It
    // fires here, on the user's pick, so a restored or auto-selected brand never shows it; and only
    // for a different brand, since re-picking the current one switches nothing.
    const nextBrand = brands.find((b) => b.id === brandId);
    if (isSwitch && nextBrand) toast.success(`You are now viewing ${nextBrand.name}.`);
    // A brand-unique detail page can't exist under the newly selected brand, so fall back to the
    // brand-level products list. Brand-level pages stay put.
    //
    // Matched through `RoutePath` rather than a literal path regex: `conventions/03-frontend-structure.md`
    // makes `RoutePath` the only place a path string is written, so renaming a detail route here
    // breaks the build instead of silently un-matching and letting the bug back in. `end: false` so
    // a nested tab (`/products/5/keywords`) still matches its parent, while the bare list route does
    // not — `:productId` is required.
    //
    // Both land on the products list. `RoutePath.KeywordLandscapes` is **not** a registered route
    // (`App.tsx` registers only `KeywordLandscapeDetail`), so navigating there would drop the user on
    // the catch-all 404; and leaving them on the landscape itself would show another brand's
    // landscape, which is the bug this fixes. Real landscapes live under `/products/:id/landscapes/:id`
    // and are already covered by the first match.
    const onBrandUniqueDetailPage = [RoutePath.ProductDetail, RoutePath.KeywordLandscapeDetail].some(
      (route) => matchPath({ path: route, end: false }, location.pathname) !== null,
    );

    if (onBrandUniqueDetailPage) {
      navigate(RoutePath.Products);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, brand: DeleteBrandTarget) => {
    e.stopPropagation();
    setBrandToDelete(brand);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-sidebar-foreground transition-colors mt-6">
            {/*
              AUT-364: `min-w-0` + `TruncatedText` so the brand name stays on ONE line at the intermediate
              widths of the collapse/expand animation. Without them it wrapped to two lines and the
              header grew and snapped back, the same defect the nav and footer labels had — and like
              them, `SidebarHeader` is a sibling of `SidebarContent`, so it inherits none of that
              element's `overflow-x-hidden`. `TruncatedText` rather than a bare `truncate` because
              `signal/no-unwrapped-clipped-text` requires clipped text to stay readable — it reveals the
              full name on hover, which matters here precisely because long brand names are the case
              this fixes. `min-w-0` is what lets the flex child shrink below its content width.
            */}
            <div className="flex items-center gap-2 min-w-0">
              <TruncatedText className="font-normal text-sm">{currentBrand?.name}</TruncatedText>
            </div>
            <ChevronDown className="h-4 w-4 opacity-60" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-0 bg-popover border border-border rounded-lg overflow-hidden [&_[cmdk-input-wrapper]]:border-b-0" sideOffset={4}>
          <Command value={brandCommandValue} onValueChange={setBrandCommandValue}>
            <CommandInput placeholder="Search brands..." className="h-9" />
            <CommandList>
              <CommandEmpty>No brand found.</CommandEmpty>
              <CommandGroup>
                {brands.map((brand) => (
                  <CommandItem
                    key={brand.id}
                    value={brand.name}
                    onSelect={() => handleBrandSelect(brand.id)}
                    className="cursor-pointer group"
                  >
                    <span className="flex-1">{brand.name}</span>
                    {brand.id === selectedBrandId && <Check className="h-4 w-4 mr-2" />}
                    {canMutateBrands && !readOnly ? (
                      <button
                        onClick={(e) => handleDeleteClick(e, { id: brand.id, name: brand.name })}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-destructive/10 rounded transition-opacity"
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </button>
                    ) : null}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <DeleteBrandDialog
        brand={brandToDelete}
        onCancel={() => setBrandToDelete(null)}
        onDeleted={() => setBrandToDelete(null)}
      />
    </>
  );
}
